import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlanActionTable from "../components/PlanActionTable";
import PlanActionModal from "../components/PlanActionModal";
import { getPlanactions } from '../services/projectService';
import { PlanAction } from "../interfaces/PlanAction";
import { AssignmentTurnedIn, Pending, Error } from '@mui/icons-material';

const PlanActionManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlanAction, setSelectedPlanAction] = useState<PlanAction | null>(null);
    const [planactions, setPlanactions] = useState<PlanAction[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchPlanAction = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getPlanactions();
            setPlanactions(response.data as PlanAction[]);
        } catch (error) {
            setError("Erreur lors de la récupération des plans d'actions");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlanAction();
    }, []);

    const handleCreatePlanAction = () => {
        setSelectedPlanAction(null);
        setIsModalOpen(true);
    };

    const handleEditPlanAction = (planAction: PlanAction) => {
        setSelectedPlanAction(planAction);
        setIsModalOpen(true);
    };

    const getStatusSummary = () => {
        const total = planactions.length;
        const completed = planactions.filter(plan => plan.status === 'COMPLETED').length;
        const inProgress = planactions.filter(plan => plan.status === 'IN_PROGRESS').length;
        const pending = planactions.filter(plan => plan.status === 'PENDING').length;

        return { total, completed, inProgress, pending };
    };

    const stats = getStatusSummary();

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des plans d'actions
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Suivez et gérez vos plans d'actions
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreatePlanAction}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Créer un plan d'action
                        </Button>
                    </Box>
                </Card>

                {/* Messages d'erreur */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Statistiques */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-gray-600 text-sm">Total</Typography>
                                    <Typography variant="h4" className="font-bold text-gray-800">
                                        {stats.total}
                                    </Typography>
                                </div>
                                <AssignmentTurnedIn className="text-gray-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-emerald-600 text-sm">Terminés</Typography>
                                    <Typography variant="h4" className="font-bold text-emerald-800">
                                        {stats.completed}
                                    </Typography>
                                </div>
                                <AssignmentTurnedIn className="text-emerald-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-blue-600 text-sm">En cours</Typography>
                                    <Typography variant="h4" className="font-bold text-blue-800">
                                        {stats.inProgress}
                                    </Typography>
                                </div>
                                <Pending className="text-blue-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-amber-600 text-sm">En attente</Typography>
                                    <Typography variant="h4" className="font-bold text-amber-800">
                                        {stats.pending}
                                    </Typography>
                                </div>
                                <Error className="text-amber-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table des plans d'action */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">
                            <PlanActionTable
                                planActions={planactions}
                                onEditPlanAction={handleEditPlanAction}
                                refreshPlanAction={fetchPlanAction}
                            />
                        </Box>
                    )}
                </Card>

                {/* Modal de création/édition */}
                <PlanActionModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        fetchPlanAction();
                    }}
                    planAction={selectedPlanAction}
                />
            </Box>
        </MainLayout>
    );
};

export default PlanActionManagement;